import { useEffect, useState } from "react";
import { SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errMsg, fmtNum, ts } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Panel, Th, Td, Empty, btn, field } from "@/components/ui-kit";

const SITES = ["XD1", "XD2", "XD3", "EP"];

const AdjustDialog = ({ item, onClose, onDone }) => {
  const [site, setSite] = useState("XD1");
  const [qty, setQty] = useState("");
  const [reason, setReason] = useState("");
  const save = async () => {
    try {
      await api.post("/inventory/adjust", { part_no: item.part_no, site_code: site, qty: Number(qty), reason });
      toast.success("Inventory adjusted");
      onDone();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md border-slate-800 bg-[#1F232C] text-slate-100" data-testid="inventory-adjust-dialog">
        <DialogHeader><DialogTitle className="font-heading">Adjust {item?.part_no}</DialogTitle></DialogHeader>
        <div className="flex gap-1.5">
          {SITES.map((s) => (
            <button key={s} onClick={() => setSite(s)} data-testid={`adjust-site-${s}`} className={`rounded-sm border px-3 py-1 font-mono text-xs ${site === s ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300" : "border-slate-700 text-slate-400"}`}>{s}</button>
          ))}
        </div>
        <Input type="number" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="+/- quantity (e.g. 120 or -5)" className={field} data-testid="adjust-qty-input" />
        <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (opening balance, cycle count…)" className={field} data-testid="adjust-reason-input" />
        <Button onClick={save} className={btn.primary} data-testid="adjust-save-btn">Post adjustment</Button>
      </DialogContent>
    </Dialog>
  );
};

export default function Inventory() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [moves, setMoves] = useState([]);
  const [tab, setTab] = useState("FG");
  const [adj, setAdj] = useState(null);
  const load = () => {
    api.get("/inventory").then((r) => setD(r.data));
    api.get("/inventory/moves").then((r) => setMoves(r.data));
  };
  useEffect(load, []);
  const rows = (d?.rows || []).filter((r) => r.type === tab);

  return (
    <div className="space-y-6">
      <PageHeader title="Inventory by site" subtitle={`Finished goods and consigned raw materials at Group O xDock sites. Min cover ${d?.threshold_days ?? "—"} days${d?.peak ? " (peak 1 Oct – 15 Jan)" : ""}.`} />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#252A35]">
          <TabsTrigger value="FG" data-testid="inventory-tab-fg">Finished goods</TabsTrigger>
          <TabsTrigger value="RM" data-testid="inventory-tab-rm">Raw materials</TabsTrigger>
        </TabsList>
      </Tabs>
      <Panel>
        {rows.length ? (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="inventory-table">
              <thead className="border-b border-slate-800">
                <tr><Th>Part</Th><Th>Description</Th>{SITES.map((s) => <Th key={s} className="text-right">{s === "EP" ? "EP plant" : s}</Th>)}<Th className="text-right">xDock total</Th><Th className="text-right">Days cover</Th><Th></Th></tr>
              </thead>
              <tbody className="divide-y divide-slate-800/70">
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`inventory-row-${r.part_no}`}>
                    <Td className="font-mono text-slate-100">{r.part_no}</Td>
                    <Td className="max-w-[240px] truncate">{r.description}</Td>
                    {SITES.map((s) => (
                      <Td key={s} className="text-right font-mono" data-testid={`inv-${r.part_no}-${s}`}>
                        {fmtNum(r.sites[s]?.on_hand || 0)}
                        {r.sites[s]?.in_transit ? <div className="text-[10px] text-sky-300">+{fmtNum(r.sites[s].in_transit)} transit</div> : null}
                        {r.site_cover?.[s] != null ? <div className={`text-[10px] ${r.site_cover[s] < (d?.threshold_days || 0) ? "text-rose-300" : "text-green-300"}`} data-testid={`inv-cover-${r.part_no}-${s}`}>{r.site_cover[s]}d cover</div> : null}
                      </Td>
                    ))}
                    <Td className="text-right font-mono text-slate-100">{fmtNum(r.xdock_on_hand)}</Td>
                    <Td className={`text-right font-mono ${r.low ? "text-rose-300" : "text-green-300"}`}>{r.days_cover ?? "—"}{r.forecast_source && <div className="text-[10px] text-slate-500">{r.forecast_source}</div>}</Td>
                    <Td className="text-right">
                      {user.role === "manager" && (
                        <Button size="sm" variant="ghost" onClick={() => setAdj(r)} className={btn.ghost} data-testid={`inventory-adjust-${r.part_no}`}><SlidersHorizontal className="h-3.5 w-3.5" /><span className="sr-only">Adjust</span></Button>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <Empty testId="inventory-empty">No items of this type.</Empty>}
      </Panel>
      <Panel title="Recent movements" testId="inventory-moves-panel">
        {moves.length ? (
          <div className="max-h-80 overflow-auto">
            <table className="w-full">
              <thead className="border-b border-slate-800"><tr><Th>When</Th><Th>Part</Th><Th>Site</Th><Th className="text-right">On hand</Th><Th className="text-right">Transit</Th><Th>Reason</Th><Th>Ref</Th></tr></thead>
              <tbody className="divide-y divide-slate-800/70">
                {moves.map((m) => (
                  <tr key={m.id}>
                    <Td className="whitespace-nowrap font-mono text-slate-500">{ts(m.at)}</Td><Td className="font-mono">{m.part_no}</Td><Td className="font-mono">{m.site_code}</Td>
                    <Td className={`text-right font-mono ${m.on_hand < 0 ? "text-rose-300" : "text-green-300"}`}>{m.on_hand ? fmtNum(m.on_hand) : ""}</Td>
                    <Td className="text-right font-mono text-sky-300">{m.in_transit ? fmtNum(m.in_transit) : ""}</Td>
                    <Td>{m.reason}</Td><Td className="font-mono">{m.ref}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <Empty>No movements yet.</Empty>}
      </Panel>
      <AdjustDialog item={adj} onClose={() => setAdj(null)} onDone={load} />
    </div>
  );
}
